import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserRole } from '~/types/server-types/UserRole';

// Define the user store state interface based on UserContext from trpc.ts
export interface UserState {
    user: {
        id: number | null;
        firstName: string;
        lastName: string;
        email: string;
        role: UserRole | null;
        timezone: string;
        practitionerId: number | null;
        practitionerCategoryId: number | null;
        isAuthenticated: boolean;
    } | null
    
    // Actions
    setUser: (user: Omit<NonNullable<UserState['user']>, 'isAuthenticated'>) => void;
    clearUser: () => void;
}

// Create the user store with persistence
export const useUserStore = create<UserState>()(
    persist(
        (set, get) => ({
            // Initial state
            user: null,
            
            // Actions
            setUser: (user) => set({ 
                user: {
                    ...user,
                    timezone: user.timezone.replace(/_/, "/"),
                    isAuthenticated: true,
                }
            }),
            
            clearUser: () => set({ 
                user: {
                    id: null,
                    firstName: '',
                    lastName: '',
                    email: '',
                    role: null,
                    timezone: 'Europe/Berlin',
                    practitionerId: null,
                    practitionerCategoryId: null,
                    isAuthenticated: false
                }
            }),
        }),
        {
            name: 'user-storage',
            // Only persist necessary user data
            partialize: (state) => ({
                user: {
                    id: state.user?.id,
                    firstName: state.user?.firstName,
                    lastName: state.user?.lastName,
                    email: state.user?.email,
                    role: state.user?.role,
                    timezone: state.user?.timezone,
                    practitionerId: state.user?.practitionerId ?? null,
                    practitionerCategoryId: state.user?.practitionerCategoryId ?? null,
                    isAuthenticated: state.user?.isAuthenticated
                }
            }),
        }
    )
);
